import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  signal,
} from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { TargetLanguageFileGroup, JobFileWithCancellation } from '../../../../../../../shared/models/file-cancellation.model';

/**
 * Confirmation Modal Component
 * 
 * Displays a summary of selected files grouped by target language before submission.
 * User reviews the grouped confirmation and proceeds to reason step or cancels.
 * 
 * Inputs:
 *  - languageGroups: TargetLanguageFileGroup[] - Files grouped by language with selection state
 *  - totalSelectedFiles: number - Count of selected files for review
 * 
 * Outputs:
 *  - nextStep: void - User clicked "Next" to proceed to reason step
 *  - back: void - User clicked "Back" to return to file selection
 *  - closed: void - User closed the dialog
 * 
 * Data-testid selectors:
 *  - specific-files-confirm-modal: Dialog root
 *  - specific-files-confirm-total: Total selected files display
 *  - specific-files-confirm-group-{lang-code}: Language group container
 *  - specific-files-confirm-next: Next button
 *  - specific-files-confirm-back: Back button
 *  - specific-files-confirm-close: Close button
 */
@Component({
  selector: 'app-confirmation-modal',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './confirmation-modal.component.html',
  styleUrl: './confirmation-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationModalComponent {
  @Input() languageGroups = signal<TargetLanguageFileGroup[]>([]);
  @Input() totalSelectedFiles = signal<number>(0);
  @Output() nextStep = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private readonly fileNameCollator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
  });

  onNext(): void {
    this.nextStep.emit();
  }

  onBack(): void {
    this.back.emit();
  }

  onClose(): void {
    this.closed.emit();
  }

  /**
   * Get selected file count for a language group
   * Returns count of checked eligible files in the group
   */
  getGroupSelectedCount(group: TargetLanguageFileGroup): number {
    return group.files
      .filter((f: JobFileWithCancellation) => f.isCancellable)
      .length;
  }

  getSortedGroupFiles(group: TargetLanguageFileGroup): JobFileWithCancellation[] {
    return [...group.files].sort((first, second) =>
      this.fileNameCollator.compare(first.fileName ?? '', second.fileName ?? ''),
    );
  }

  /**
   * Convert number to words with 'files' suffix
   * Examples: 1 -> 'one file', 4 -> 'four files', 10 -> '10 files'
   */
  getFilesCountWithLabel(): string {
    const count = this.totalSelectedFiles();
    const numberWords: { [key: number]: string } = {
      1: 'one',
      2: 'two',
      3: 'three',
      4: 'four',
      5: 'five',
      6: 'six',
      7: 'seven',
      8: 'eight',
      9: 'nine',
      10: 'ten',
    };
    const word = numberWords[count] || count.toString();
    const fileWord = count === 1 ? 'file' : 'files';
    return `${word} ${fileWord}`;
  }
}
